import { LucideIcon } from 'lucide-react'
import * as React from 'react'

import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './select'

export interface ISelectOption {
  label: string | React.ReactNode
  value: string
  icon?: LucideIcon
  disabled?: boolean
}

export interface IGenericSelectProps {
  /** The currently selected value */
  value?: string
  /** Default value if uncontrolled */
  defaultValue?: string
  /** Callback when value changes */
  onValueChange?: (value: string) => void
  /** Array of options to display */
  options: ISelectOption[]
  /** Placeholder text when no option is selected */
  placeholder?: string
  /** Whether the select is disabled */
  disabled?: boolean
  /** Optional label above the select */
  label?: string
  /** Optional group label within the dropdown */
  groupLabel?: string
  /** Additional className for the trigger */
  triggerClassName?: string
  /** Additional className for the content */
  contentClassName?: string
  /** Additional className for the items */
  itemClassName?: string
  /** Custom trigger component */
  customTrigger?: React.ComponentType<{ value?: string; placeholder?: string; className?: string }>
}

/**
 * A simplified select component that wraps Radix UI Select primitives
 * with a more straightforward API for common use cases.
 */
export const GenericSelect = React.forwardRef<HTMLButtonElement, IGenericSelectProps>(
  (
    {
      value,
      defaultValue,
      onValueChange,
      options,
      placeholder = 'Select an option',
      disabled = false,
      label,
      groupLabel,
      triggerClassName,
      contentClassName,
      itemClassName,
      customTrigger: CustomTrigger
    },
    ref
  ) => {
    return (
      <Select value={value} defaultValue={defaultValue} onValueChange={onValueChange} disabled={disabled}>
        {CustomTrigger ? (
          <CustomTrigger />
        ) : (
          <SelectTrigger ref={ref} className={triggerClassName}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
        )}
        <SelectContent className={contentClassName}>
          <SelectGroup>
            {label && <SelectLabel>{label}</SelectLabel>}
            {groupLabel && <SelectLabel>{groupLabel}</SelectLabel>}
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className={itemClassName}
                disabled={option.disabled}
              >
                <div className="flex items-center gap-2">
                  {option.icon && <option.icon />}
                  {option.label}
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    )
  }
)